import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import styled, {keyframes} from "styled-components";
import {fetchPlanPrices, formatPlanPrice} from "services/WebCheckoutService";
import type {CheckoutPlan, CheckoutTier, PlanPrices} from "services/WebCheckoutService";
import {ws} from "styles/tokens";

/**
 * The full paywall from the Figma "Web sign in - Paywall selection" frame
 * (Waterspeed Website, node 846:116): hero, plan picker, CTA, feature list,
 * reviews and FAQ. It is deliberately long — the caller decides whether the
 * page scrolls (onboarding) or the card scrolls inside itself (the /my-account
 * modal, via `maxHeight`).
 */

const C = {
  ink: ws.fg,
  muted: ws.muted,
  border: ws.line,
  panel: ws.wash,
  skyTint: ws.tint,
  skyLine: ws.accent,
  /* Orange backdrop baked into the Figma hero artwork, not a UI colour */
  heroBg: "#FF5101",
  accent: ws.accent
};

const ASSETS = "/assets/images/subscription";

const Card = styled.div<{maxHeight?: string}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  border-radius: ${ws.radiusXl};
  background: ${ws.card};
  box-shadow: ${ws.shadowMenu};
  overflow-y: auto;
  overflow-x: hidden;
  ${(props) => (props.maxHeight ? `max-height: ${props.maxHeight};` : "")}
  /* Keep the rounded corners visible while the content scrolls under them */
  -webkit-overflow-scrolling: touch;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 38px 0 24px;
`;

/* ---------------------------------------------------------------- hero ---- */

const Block = styled.section<{gap?: string; padding?: string; bg?: string; bordered?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap || "16px"};
  padding: ${(props) => props.padding || "20px"};
  ${(props) => (props.bg ? `background: ${props.bg};` : "")}
  ${(props) => (props.bordered ? `border: 1px solid ${C.border};` : "")}
`;

const Headline = styled.h2`
  margin: 0;
  font-family: ${ws.fontDisplay};
  font-size: 29px;
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${C.ink};
  text-align: center;
`;

const Lede = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 17px;
  font-weight: 400;
  color: ${C.muted};
  text-align: center;
`;

/* Orange card holding the action shot with two phone mockups bleeding off the
   bottom edge. Everything is positioned in % of the 440x283 Figma frame so it
   scales with the card. */
const HeroArt = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 440 / 283;
  border-radius: ${ws.radiusLg};
  background: ${C.heroBg};
  box-shadow: ${ws.shadowSoft};
  overflow: hidden;
`;

const HeroPhoto = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 88.85%;
  border-radius: ${ws.radiusMd};
  object-fit: cover;
`;

const HeroPhone = styled.div<{left: string; width: string}>`
  position: absolute;
  top: 6.23%;
  left: ${(props) => props.left};
  width: ${(props) => props.width};
  height: 94.57%;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

/* -------------------------------------------------------------- picker ---- */

/* Same look as the design system's SegmentedTabs (ws-tabs) */
const PlanToggle = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${C.border};
  border-radius: ${ws.radiusPill};
  background: ${C.panel};
`;

const PlanTab = styled.button<{active: boolean}>`
  flex: 1;
  padding: 8px;
  border: 0;
  border-radius: ${ws.radiusPill};
  background: ${(props) => (props.active ? ws.card : "transparent")};
  box-shadow: ${(props) => (props.active ? ws.shadowTab : "none")};
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 600 : 500)};
  color: ${(props) => (props.active ? C.ink : C.muted)};
  cursor: pointer;
`;

const TierList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TierRow = styled.div<{selected: boolean}>`
  display: flex;
  flex-direction: column;
  border: ${(props) => (props.selected ? `2px solid ${C.accent}` : `1px solid ${C.border}`)};
  border-radius: ${ws.radiusMd};
  background: ${ws.card};
  overflow: hidden;
  cursor: pointer;
`;

const TierBanner = styled.div`
  padding: 4px 12px;
  background: ${C.accent};
  font-family: ${ws.fontBody};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${ws.onAccent};
  text-align: center;
  text-transform: uppercase;
`;

const TierBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
`;

const TierIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

/* The Figma ellipse is a radio control, so it is drawn rather than imported —
   it has to render both states and follow the selection. */
const Radio = styled.span<{selected: boolean}>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid ${(props) => (props.selected ? C.accent : ws.lineStrong)};
  border-radius: 50%;

  &::after {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => (props.selected ? C.accent : "transparent")};
  }
`;

const TierNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TierName = styled.span`
  font-family: ${ws.fontBody};
  font-size: 16px;
  font-weight: 600;
  color: ${C.ink};
`;

const TierChip = styled.span`
  padding: 2px 7px;
  border-radius: ${ws.radiusXs};
  background: ${C.skyTint};
  font-family: ${ws.fontBody};
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${ws.accentText};
  text-transform: uppercase;
  white-space: nowrap;
`;

const TierSubline = styled.span`
  font-family: ${ws.fontBody};
  font-size: 12px;
  font-weight: 400;
  color: ${C.muted};
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const PriceSpinner = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2px solid ${C.muted};
  border-top-color: ${C.accent};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const TierPrice = styled.span`
  flex-shrink: 0;
  font-family: ${ws.fontDisplay};
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${C.ink};
  white-space: nowrap;
`;

const CtaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 51px;
  padding: 0 36px;
  border: none;
  border-radius: ${ws.radiusPill};
  background: ${ws.accent};
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${ws.onAccent};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${ws.accentDeep};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/* The Figma arrow asset points up; the CTA rotates it to point right */
const CtaArrow = styled.img`
  width: 16px;
  height: 16px;
  transform: rotate(90deg);
`;

const Terms = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 13px;
  color: ${C.muted};
  text-align: center;
`;

const TrustBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const TrustTitle = styled.span`
  font-family: ${ws.fontBody};
  font-size: 12px;
  font-weight: 600;
  color: ${C.ink};
`;

const PayRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const PayBadge = styled.span`
  padding: 4px 8px;
  border: 1px solid ${C.border};
  border-radius: ${ws.radiusXs};
  background: ${ws.card};
  font-family: ${ws.fontBody};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${C.muted};
  text-transform: uppercase;
`;

/* Secondary action: outlined pill */
const DismissButton = styled.button`
  align-self: center;
  padding: 10px 22px;
  border: 1px solid ${ws.lineStrong};
  border-radius: ${ws.radiusPill};
  background: transparent;
  font-family: ${ws.fontBody};
  font-size: 15px;
  font-weight: 600;
  color: ${C.ink};
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${C.ink};
  }
`;

/* ------------------------------------------------------ marketing blocks -- */

const SectionTitle = styled.h3<{center?: boolean}>`
  margin: 0;
  font-family: ${ws.fontDisplay};
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: ${C.ink};
  text-align: ${(props) => (props.center ? "center" : "left")};
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const FeatureIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${ws.radiusXs};
  background: ${C.skyTint};

  img {
    width: 14px;
    height: 14px;
  }
`;

const BodyText = styled.p`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-family: ${ws.fontBody};
  font-size: 13px;
  line-height: 1.4;
  color: ${C.muted};
`;

const ReviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid ${C.border};
  border-radius: ${ws.radiusMd};
  background: ${ws.card};
`;

const ReviewHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ReviewTitle = styled.span`
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${C.ink};
`;

const Stars = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 4px;

  img {
    width: 14px;
    height: 14px;
  }
`;

const ReviewQuote = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 12px;
  line-height: 1.4;
  color: ${C.muted};
`;

const ReviewAuthor = styled.span`
  font-family: ${ws.fontBody};
  font-size: 11px;
  font-weight: 600;
  color: ${C.ink};
`;

const FaqItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
`;

const FaqQuestion = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  border: none;
  background: none;
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${C.ink};
  text-align: left;
  cursor: pointer;
`;

const Chevron = styled.img<{open: boolean}>`
  flex-shrink: 0;
  width: 12px;
  height: 8px;
  transition: transform 0.15s ease;
  transform: rotate(${(props) => (props.open ? "180deg" : "0deg")});
`;

const FaqAnswer = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 12px;
  line-height: 1.4;
  color: ${C.muted};
`;

/* ----------------------------------------------------------------- data --- */

const FEATURES = [
  {icon: "icon-activity.svg", key: "featureDashboards"},
  {icon: "icon-cloud-rain.svg", key: "featureWeather"},
  {icon: "icon-users.svg", key: "featureCommunity"},
  {icon: "icon-map-pin.svg", key: "featureSpots"},
  {icon: "icon-shield.svg", key: "featureSafety"}
] as const;

/* Verbatim App Store reviews — the author handles are data, not copy */
const REVIEWS = [
  {key: "reviewSessions", author: "e in CA"},
  {key: "reviewMustHave", author: "dedge64"},
  {key: "reviewData", author: "Wywiwa"}
] as const;

const FAQ_KEYS = ["faqIncludes", "faqNotAPlan", "faqSports", "faqCancel"] as const;

const PAYMENT_BRANDS = ["visa", "mastercard", "amex", "apple-pay", "google-pay"];

const TIER_BADGE: Record<CheckoutTier, "banner" | "chip"> = {premium: "banner", ultra: "chip"};

interface Props {
  /** Tiers to offer, in display order. */
  tiers: CheckoutTier[];
  initialTier?: CheckoutTier;
  /** Show the free-trial wording — first-time subscribers only. */
  trialEligible?: boolean;
  /**
   * The user already has a web subscription, so this changes the plan on it
   * (prorated) rather than starting a new one. Switches the CTA and terms copy.
   */
  changingExisting?: boolean;
  /** The change ends a free trial and charges today, so the terms say so instead of the proration note. */
  changeEndsTrial?: boolean;
  /** Upgrades bill the prorated difference today (Paddle) rather than on the next invoice (Stripe). */
  upgradeBilledNow?: boolean;
  isSubmitting?: boolean;
  onSubscribe: (tier: CheckoutTier, plan: CheckoutPlan) => void;
  onCancel?: () => void;
  /** Overrides the dismiss label (onboarding says "No thanks", not "Not now"). */
  cancelLabel?: string;
  /**
   * Placement the prices are read through — the same one the purchase resolves
   * against, so an A/B test cannot show one price and charge another.
   */
  placementId?: string;
  /**
   * Caps the card and scrolls its content instead of the page. Set it when the
   * card sits in a modal; leave it off to let the page scroll.
   */
  maxHeight?: string;
}

function PlanPickerCard({
  tiers,
  initialTier,
  trialEligible = false,
  changingExisting = false,
  changeEndsTrial = false,
  upgradeBilledNow = false,
  isSubmitting,
  onSubscribe,
  onCancel,
  cancelLabel,
  placementId,
  maxHeight
}: Props) {
  const {t, i18n} = useTranslation();
  const [tier, setTier] = useState<CheckoutTier>(initialTier && tiers.includes(initialTier) ? initialTier : tiers[0]);
  const [plan, setPlan] = useState<CheckoutPlan>("yearly");
  const [prices, setPrices] = useState<PlanPrices | null>(null);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPricesLoading(true);
    (async () => {
      const result = await fetchPlanPrices(placementId);
      if (!cancelled) {
        setPrices(result);
        setPricesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [placementId]);

  const period = plan === "yearly" ? t("subscribe.perYear") : t("subscribe.perMonth");
  const priceFor = (option: CheckoutTier) => formatPlanPrice(prices?.[option]?.[plan], i18n.language);

  const sublineFor = (option: CheckoutTier) => {
    const price = priceFor(option);
    if (!price) {
      return trialEligible ? t("subscribe.trialTerms") : t("subscribe.autoRenews");
    }
    const priceLine = t("subscribe.priceLine", {price, period});
    return trialEligible ? t("subscribe.sublineTrial", {priceLine}) : t("subscribe.sublineRenewing", {priceLine});
  };

  const changeTerms = () => {
    if (!changeEndsTrial) {
      return upgradeBilledNow ? t("subscribe.prorationNoteUpgradeNow") : t("subscribe.prorationNote");
    }
    const price = priceFor(tier);
    return price
      ? `${t("subscribe.endsTrialNote")} ${t("subscribe.chargedToday", {price})}`
      : t("subscribe.endsTrialNote");
  };

  const ctaLabel = changingExisting
    ? t("subscribe.confirmChange")
    : trialEligible
      ? t("subscribe.startTrial")
      : t("subscribe.subscribe");

  return (
    <Card maxHeight={maxHeight}>
      <Inner>
        <Block>
          <Headline>{changingExisting ? t("subscribe.changeTitle") : t("subscribe.heroTitle")}</Headline>
          <Lede>{t("subscribe.heroSubtitle")}</Lede>
          <HeroArt>
            <HeroPhoto src={`${ASSETS}/hero-bg.png`} alt="" />
            <HeroPhone left="10.79%" width="45.4%">
              <img src={`${ASSETS}/hero-phone-left.png`} alt="" />
            </HeroPhone>
            <HeroPhone left="44.11%" width="44.99%">
              <img src={`${ASSETS}/hero-phone-right.png`} alt="" />
            </HeroPhone>
          </HeroArt>
        </Block>

        <Block padding="12px 20px">
          <Headline>{t("subscribe.choosePlan")}</Headline>

          <PlanToggle>
            <PlanTab active={plan === "yearly"} onClick={() => setPlan("yearly")}>
              {t("subscribe.planYearly")}
            </PlanTab>
            <PlanTab active={plan === "monthly"} onClick={() => setPlan("monthly")}>
              {t("subscribe.planMonthly")}
            </PlanTab>
          </PlanToggle>

          <TierList>
            {tiers.map((option) => {
              const selected = tier === option;
              const label = option === "ultra" ? t("subscribe.tierUltra") : t("subscribe.tierPro");
              const badge = TIER_BADGE[option];
              return (
                <TierRow key={option} selected={selected} onClick={() => setTier(option)}>
                  {/* Figma only shows the banner on the selected card, so it follows the selection */}
                  {selected && badge === "banner" && <TierBanner>{t("subscribe.badgePopular")}</TierBanner>}
                  <TierBody>
                    <TierIdentity>
                      <Radio selected={selected} />
                      <div>
                        <TierNameRow>
                          <TierName>{label}</TierName>
                          {badge === "chip" && <TierChip>{t("subscribe.badgeBestValue")}</TierChip>}
                        </TierNameRow>
                        <TierSubline>{sublineFor(option)}</TierSubline>
                      </div>
                    </TierIdentity>
                    {pricesLoading ? (
                      <PriceSpinner role="status" aria-label={t("common.loading")} />
                    ) : (
                      priceFor(option) && <TierPrice>{priceFor(option)}</TierPrice>
                    )}
                  </TierBody>
                </TierRow>
              );
            })}
          </TierList>

          {changingExisting && <Terms>{changeTerms()}</Terms>}

          <CtaButton disabled={isSubmitting} onClick={() => onSubscribe(tier, plan)}>
            {ctaLabel}
            <CtaArrow src="/assets/images/onboarding/arrow-cta.svg" alt="" />
          </CtaButton>

          <TrustBlock>
            <TrustTitle>{t("subscribe.storeRating")}</TrustTitle>
            <PayRow>
              {PAYMENT_BRANDS.map((brand) => (
                <PayBadge key={brand}>{brand}</PayBadge>
              ))}
            </PayRow>
          </TrustBlock>

          {onCancel && <DismissButton onClick={onCancel}>{cancelLabel || t("subscribe.notNow")}</DismissButton>}
        </Block>

        <Block bordered>
          <SectionTitle>{t("subscribe.insideTitle")}</SectionTitle>
          {FEATURES.map((feature) => (
            <FeatureRow key={feature.key}>
              <FeatureIcon>
                <img src={`${ASSETS}/${feature.icon}`} alt="" />
              </FeatureIcon>
              <BodyText>{t(`subscribe.${feature.key}`)}</BodyText>
            </FeatureRow>
          ))}
        </Block>

        <Block bg={C.panel}>
          <SectionTitle>{t("subscribe.reviewsTitle")}</SectionTitle>
          {REVIEWS.map((review) => (
            <ReviewCard key={review.key}>
              <ReviewHead>
                <ReviewTitle>{t(`subscribe.${review.key}Title`)}</ReviewTitle>
                <Stars>
                  {[0, 1, 2, 3, 4].map((index) => (
                    <img key={index} src={`${ASSETS}/icon-star.svg`} alt="" />
                  ))}
                </Stars>
              </ReviewHead>
              <ReviewQuote>{t(`subscribe.${review.key}Quote`)}</ReviewQuote>
              <ReviewAuthor>{review.author}</ReviewAuthor>
            </ReviewCard>
          ))}
        </Block>

        <Block gap="24px">
          <SectionTitle center>{t("subscribe.faqTitle")}</SectionTitle>
          {FAQ_KEYS.map((key) => {
            const open = openFaq === key;
            return (
              <FaqItem key={key}>
                <FaqQuestion onClick={() => setOpenFaq(open ? null : key)} aria-expanded={open}>
                  {t(`subscribe.${key}Q`)}
                  <Chevron src={`${ASSETS}/icon-chevron-down.svg`} alt="" open={open} />
                </FaqQuestion>
                {open && <FaqAnswer>{t(`subscribe.${key}A`)}</FaqAnswer>}
              </FaqItem>
            );
          })}
        </Block>
      </Inner>
    </Card>
  );
}

export default PlanPickerCard;
